import { useTranslation } from 'react-i18next';

import { useGetLongAbsences } from '~/data/absences/useGetLongAbsences';
import { useGetShortAbsences } from '~/data/absences/useGetShortAbsences';
import { useCompanyData, useModulesConfig } from '~/data/company';
import { useGetCompanyOffices } from '~/data/companyOffices';
import { useAllCostCenters } from '~/data/costCenters';
import { useCustomPositionFields } from '~/data/customData';
import { useCustomDataAttributes } from '~/data/customData/useCustomDataAttributes';
import { useCustomEmployeeData } from '~/data/customData/useCustomEmployeeData';
import { useCustomFieldTranslations } from '~/data/customData/useCustomFieldTranslations';
import { useCustomPositionFieldAttributes } from '~/data/customData/useCustomPositionFieldAttributes';
import { useCustomPositionFieldTranslations } from '~/data/customData/useCustomPositionFieldTranslations';
import { useEmployee } from '~/data/employee';
import { useAllEmployees } from '~/data/employees';
import { useGetFullEmploymentData } from '~/data/employment/useFullEmploymentData';
import { useAllEnums } from '~/data/enums';
import { useEmployeeHistory, useEmployeeOtherHistory } from '~/data/history';
import { useAllLegalEntities } from '~/data/legalEntity';
import { useAllOrgUnits } from '~/data/orgunits';
import { useSelectedEmployeeId } from '~/hooks/employee';
import { getCurrentLanguage } from '~/localization';
import { AppModules } from '~/services/Permissions';

export const useGetPlaceholderDataDocx = (
  includeOnlyActiveLanguageKeys = false,
  employmentId?: number,
) => {
  const { selectedEmployeeId: empId } = useSelectedEmployeeId();
  const { t } = useTranslation();

  const { allEnums: jsonEnumALL } = useAllEnums();

  const { allEmployees: jsonAllEmp } = useAllEmployees();

  const { data: jsonOffices } = useGetCompanyOffices();

  const {
    orgUnits: jsonOrgUnitData,
    orgUnitsPrimary: jsonPrimaryUnitData,
    orgUnitsIncludeDeleted: jsonOrgUnitDataInclDeleted,
  } = useAllOrgUnits();

  const { legalEntities: jsonLegalEntities } = useAllLegalEntities();

  const { isModuleEnabled } = useModulesConfig();

  const { costCenters: jsonCostCenters } = useAllCostCenters();

  const { companyData: jsonCompanyData } = useCompanyData();

  const isCustomDataEnabled = isModuleEnabled(AppModules.CUSTOM_DATA);

  const enumLanguages = includeOnlyActiveLanguageKeys
    ? [{ fComLanguageCode: getCurrentLanguage() }]
    : jsonCompanyData.languages || [];

  const {
    employee: jsonContractEmployeeData,
    isLoading: isEmployeeDataLoading,
  } = useEmployee();
  const {
    data: jsonContractEmploymentData,
    isLoading: isEmploymentDataLoading,
  } = useGetFullEmploymentData({ employeeId: empId, employmentId });
  const {
    data: jsonContractDevHistoricalData,
    isLoading: isHistoricalDataLoading,
  } = useEmployeeHistory({ employeeId: empId });
  const {
    data: jsonContractDevCurrentData,
    isLoading: isOtherHistoricalDataLoading,
  } = useEmployeeHistory({ employeeId: empId, period: 'current' });

  const {
    data: jsonContractCustomDataPeople,
    isLoading: isCustomEmployeeDataLoading,
  } = useCustomEmployeeData(empId);
  const {
    data: jsonCustomContractEmployeeFieldAttr,
    isLoading: isCustomAttrLoading,
  } = useCustomDataAttributes();
  const {
    data: jsonCustomContractEmployeeFieldsTranslation,
    isLoading: isCustomFieldsTranslationLoading,
  } = useCustomFieldTranslations(
    enumLanguages.map((lang) => lang.fComLanguageCode),
  );
  const {
    data: jsonContractCustomDataPosition,
    isLoading: isCustomDataPositionLoading,
  } = useCustomPositionFields(empId);
  const {
    data: jsonCustomContractPositionFieldAttr,
    isLoading: isCustomPositionAttrLoading,
  } = useCustomPositionFieldAttributes();
  const {
    data: jsonCustomContractPositionFieldsTranslation,
    isLoading: isCustomPositionTranslationLoading,
  } = useCustomPositionFieldTranslations(
    enumLanguages.map((lang) => lang.fComLanguageCode),
  );

  const { data: jsonShortAbsences, isLoading: isLoadingShortAbsences } =
    useGetShortAbsences({ employeeId: empId });
  const { data: jsonLongAbsences, isLoading: isLoadingLongAbsences } =
    useGetLongAbsences({ employeeId: empId });
  const {
    data: jsonDevOtherExperienceData,
    isLoading: isOtherExperienceDataLoading,
  } = useEmployeeOtherHistory({ employeeId: empId });

  const isLoading =
    isEmployeeDataLoading ||
    isEmploymentDataLoading ||
    isHistoricalDataLoading ||
    isOtherHistoricalDataLoading ||
    isCustomEmployeeDataLoading ||
    isCustomAttrLoading ||
    isCustomFieldsTranslationLoading ||
    isCustomDataPositionLoading ||
    isCustomPositionAttrLoading ||
    isCustomPositionTranslationLoading ||
    isLoadingShortAbsences ||
    isLoadingLongAbsences ||
    isOtherExperienceDataLoading;

  return {
    empId,
    isCustomDataEnabled,
    jsonEnumALL,
    jsonAllEmp,
    jsonOrgUnitData,
    jsonPrimaryUnitData,
    jsonOrgUnitDataInclDeleted,
    jsonOffices,
    t,
    jsonLegalEntities,
    jsonCostCenters,
    jsonContractEmployeeData,
    jsonContractEmploymentData,
    jsonContractDevHistoricalData,
    jsonContractDevCurrentData,
    jsonContractCustomDataPeople,
    jsonCustomContractEmployeeFieldAttr,
    jsonCustomContractEmployeeFieldsTranslation,
    jsonContractCustomDataPosition,
    jsonCustomContractPositionFieldAttr,
    jsonCustomContractPositionFieldsTranslation,
    jsonShortAbsences,
    jsonLongAbsences,
    jsonDevOtherExperienceData,
    isLoading,
  };
};
